import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { api, apiErr } from "../lib/api";
import { PortalNav } from "../components/PortalNav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { enablePush, disablePush, isPushEnabled, pushSupported } from "../lib/push";
import { toast } from "sonner";
import { Package, MapPin, Tag, LogOut, Plus, Trash2, ArrowRight, Loader2, Heart, Bell } from "lucide-react";

const STATUS = { pending: "In attesa", confirmed: "In attesa di conferma", accepted: "Accettato", preparing: "In preparazione", ready: "Pronto", out_for_delivery: "In consegna", delivered: "Consegnato", completed: "Completato", rejected: "Rifiutato", cancelled: "Annullato" };

function Section({ icon: Icon, title, children, action }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.04] rounded-2xl border border-white/10 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-head text-lg font-bold flex items-center gap-2"><Icon size={18} className="text-[#FFC400]" /> {title}</h2>
        {action}
      </div>
      {children}
    </motion.div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ open: false, label: "", address: "", cap: "", city: "" });
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const loadAddresses = () => api.get("/account/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  const loadFavorites = () => api.get("/account/favorites").then((r) => setFavorites(r.data)).catch(() => {});
  const loadNotifs = () => api.get("/account/notifications").then((r) => setNotifs(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/account/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(apiErr(e))),
      loadAddresses(),
      loadFavorites(),
      loadNotifs(),
      api.get("/portal/coupons").then((r) => setCoupons(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { isPushEnabled().then(setPushOn); }, []);

  const togglePush = async (on) => {
    setPushBusy(true);
    try {
      if (on) { await enablePush(); setPushOn(true); toast.success("Notifiche attivate"); }
      else { await disablePush(); setPushOn(false); toast.success("Notifiche disattivate"); }
    } catch (e) {
      toast.error(e?.message || "Operazione non riuscita");
      setPushOn(await isPushEnabled());
    } finally { setPushBusy(false); }
  };

  const markNotifsRead = async () => {
    try { await api.post("/account/notifications/read"); setNotifs((p) => p.map((n) => ({ ...n, read: true }))); } catch (e) { toast.error(apiErr(e)); }
  };

  const resendVerify = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Email di conferma inviata, controlla la posta"); } catch (e) { toast.error(apiErr(e)); }
  };

  const delFavorite = async (f) => {
    try { await api.delete(`/account/favorites/${f.tenant_slug}/${f.store_slug}`); loadFavorites(); } catch (e) { toast.error(apiErr(e)); }
  };

  const addAddress = async () => {
    if (!form.label.trim() || !form.address.trim()) { toast.error("Inserisci etichetta e indirizzo"); return; }
    try {
      await api.post("/account/addresses", { label: form.label, address: form.address, cap: form.cap, city: form.city });
      setForm({ open: false, label: "", address: "", cap: "", city: "" });
      loadAddresses();
      toast.success("Indirizzo salvato");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delAddress = async (id) => {
    try { await api.delete(`/account/addresses/${id}`); loadAddresses(); } catch (e) { toast.error(apiErr(e)); }
  };
  const doLogout = async () => { nav("/"); await logout(); };

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <PortalNav />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-head text-3xl font-extrabold">Ciao, {user?.name?.split(" ")[0] || "cliente"} 👋</h1>
            <p className="text-white/55 text-sm mt-1">{user?.email}</p>
          </div>
          <Button variant="ghost" onClick={doLogout} className="text-white/70 hover:text-white hover:bg-white/10 rounded-full" data-testid="account-logout"><LogOut size={16} className="mr-1.5" />Esci</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FFC400]" /></div>
        ) : (
          <div className="space-y-5">
            <Section icon={Bell} title={`Notifiche${notifs.filter((n) => !n.read).length ? ` (${notifs.filter((n) => !n.read).length})` : ""}`}
              action={notifs.some((n) => !n.read) ? <button onClick={markNotifsRead} className="text-sm font-semibold text-[#FFC400] hover:underline" data-testid="account-notifs-read">Segna tutte lette</button> : null}>
              {notifs.length === 0 ? (
                <p className="text-white/50 text-sm" data-testid="account-notifs-empty">Nessuna notifica. Salva i tuoi locali preferiti per ricevere le nuove offerte.</p>
              ) : (
                <div className="space-y-2" data-testid="account-notifs">
                  {notifs.map((n) => (
                    <button key={n.id} onClick={() => n.link && nav(n.link)} data-testid={`account-notif-${n.id}`}
                      className={`w-full text-left flex gap-3 rounded-xl border px-4 py-3 transition-colors ${n.read ? "border-white/10 bg-white/[0.02]" : "border-[#FFC400]/40 bg-[#FFC400]/[0.06]"}`}>
                      <span className="w-9 h-9 shrink-0 rounded-lg bg-[#FFC400]/15 grid place-items-center text-[#FFC400]"><Tag size={16} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-white line-clamp-1">{n.title}</span>
                          {!n.read && <span className="w-2 h-2 rounded-full bg-[#FFC400] shrink-0" />}
                        </span>
                        <span className="block text-xs text-white/60">{n.body}</span>
                        <span className="block text-[11px] text-white/40 mt-0.5">{new Date(n.created_at).toLocaleString("it-IT")}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Bell} title="Notifiche push">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-white/60">{pushSupported() ? "Ricevi aggiornamenti sui tuoi ordini anche ad app chiusa o schermo spento." : "Il tuo browser non supporta le notifiche push."}</p>
                <Switch checked={pushOn} disabled={!pushSupported() || pushBusy} onCheckedChange={togglePush} data-testid="account-push-toggle" />
              </div>
            </Section>

            {user && user.email_verified === false && (
              <div className="rounded-2xl border border-[#FFC400]/40 bg-[#FFC400]/[0.08] p-4 flex items-center gap-3" data-testid="verify-banner">
                <Bell size={18} className="text-[#FFC400] shrink-0" />
                <p className="text-sm text-white/80 flex-1">Conferma il tuo indirizzo email per attivare tutte le funzioni. Controlla la tua posta.</p>
                <Button size="sm" onClick={resendVerify} data-testid="verify-resend" className="bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] font-semibold rounded-full shrink-0">Invia di nuovo</Button>
              </div>
            )}

            <Section icon={Package} title="I miei ordini">
              {orders.length === 0 ? (
                <p className="text-white/50 text-sm" data-testid="account-orders-empty">Nessun ordine ancora. <Link to="/" className="text-[#FFC400] hover:underline">Scopri i locali →</Link></p>
              ) : (
                <div className="space-y-2" data-testid="account-orders">
                  {orders.map((o) => (
                    <Link key={o.public_token} to={`/order/${o.public_token}`} data-testid={`account-order-${o.short_code}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 hover:border-white/20 transition-colors">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">#{o.short_code} · {o.store_name || o.brand}</p>
                        <p className="text-xs text-white/50">{o.items_count} articoli · {o.order_type === "delivery" ? "Consegna" : "Ritiro"} · {new Date(o.created_at).toLocaleDateString("it-IT")}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-sm">€{Number(o.total || 0).toFixed(2)}</p>
                        <span className="text-[11px] text-[#FFC400] font-semibold">{STATUS[o.op_status] || o.op_status}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={MapPin} title="Indirizzi salvati" action={
              <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, open: !f.open }))} data-testid="account-add-address-btn"
                className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/10 h-8"><Plus size={14} className="mr-1" />Aggiungi</Button>
            }>
              {form.open && (
                <div className="grid sm:grid-cols-2 gap-2 mb-4 p-3 rounded-xl bg-white/[0.03] border border-white/10" data-testid="account-address-form">
                  <Input placeholder="Etichetta (es. Casa)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-white/35" data-testid="account-addr-label" />
                  <Input placeholder="Indirizzo" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-white/35" data-testid="account-addr-address" />
                  <Input placeholder="CAP" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-white/35" data-testid="account-addr-cap" />
                  <Input placeholder="Città" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-white/35" data-testid="account-addr-city" />
                  <Button onClick={addAddress} className="sm:col-span-2 rounded-full bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] font-semibold" data-testid="account-addr-save">Salva indirizzo</Button>
                </div>
              )}
              {addresses.length === 0 && !form.open ? (
                <p className="text-white/50 text-sm" data-testid="account-addresses-empty">Nessun indirizzo salvato.</p>
              ) : (
                <div className="space-y-2">
                  {addresses.map((a) => (
                    <div key={a.id} className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-3" data-testid={`account-address-${a.id}`}>
                      <div><p className="font-semibold text-sm">{a.label}</p><p className="text-xs text-white/50">{a.address}{a.cap ? `, ${a.cap}` : ""}{a.city ? ` ${a.city}` : ""}</p></div>
                      <button onClick={() => delAddress(a.id)} className="text-white/40 hover:text-red-400" data-testid={`account-address-del-${a.id}`}><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Heart} title="Locali preferiti">
              {favorites.length === 0 ? (
                <p className="text-white/50 text-sm" data-testid="account-favorites-empty">Nessun preferito. Tocca il cuore su un locale per salvarlo e riordinare in un tap.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="account-favorites">
                  {favorites.map((f) => (
                    <div key={`${f.tenant_slug}-${f.store_slug}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={`account-fav-${f.store_slug}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{f.name}</p>
                        <p className="text-xs text-white/50 truncate">{f.brand} · {f.address}</p>
                        <span className={`text-[11px] font-semibold ${f.open ? "text-green-300" : "text-white/40"}`}>{f.open ? "Aperto" : "Chiuso"}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link to={`/locale/${f.tenant_slug}/${f.store_slug}`} data-testid={`account-reorder-${f.store_slug}`}>
                          <Button size="sm" className="rounded-full bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] h-8 font-semibold">Riordina</Button>
                        </Link>
                        <button onClick={() => delFavorite(f)} className="text-white/40 hover:text-red-400" data-testid={`account-fav-del-${f.store_slug}`}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Tag} title="Coupon disponibili" action={<Link to="/account/coupon" className="text-sm font-semibold text-[#FFC400] hover:underline" data-testid="account-wallet-link">Portafoglio coupon →</Link>}>
              {coupons.length === 0 ? (
                <p className="text-white/50 text-sm" data-testid="account-coupons-empty">Nessun coupon attivo al momento.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="account-coupons">
                  {coupons.slice(0, 6).map((c, i) => (
                    <Link key={`${c.code}-${i}`} to={`/locale/${c.tenant_slug}/${c.store_slug}`}
                      className="flex items-center justify-between rounded-xl border border-dashed border-[#FFC400]/40 bg-[#FFC400]/[0.06] px-4 py-3 hover:bg-[#FFC400]/10 transition-colors">
                      <div><p className="font-bold text-[#FFC400]">{c.code}</p><p className="text-xs text-white/50">{c.store_name}</p></div>
                      <ArrowRight size={16} className="text-[#FFC400]" />
                    </Link>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
